import { Mood, MatchStatus } from "../types";
import { useLanguage } from "../contexts/LanguageContext";
import { useCountryTranslations } from "../contexts/CountryTranslationsContext";

interface PredictionCardProps {
  homeTeam: string;
  awayTeam: string;
  homeFlag: string;
  awayFlag: string;
  homeScore: number;
  awayScore: number;
  mood: Mood;
  userName?: string;
  status?: MatchStatus;
}

const MOOD_EMOJIS = {
  confident: "😎",
  nervous: "😰",
  bold: "🎲",
};

export function PredictionCard({
  homeTeam,
  awayTeam,
  homeFlag,
  awayFlag,
  homeScore,
  awayScore,
  mood,
  userName: _userName = "You",
  status: _status,
}: PredictionCardProps) {
  const { t } = useLanguage();
  const { getCountryName } = useCountryTranslations();

  const translatedHomeTeam = getCountryName(homeTeam);
  const translatedAwayTeam = getCountryName(awayTeam);

  return (
    <div className="gradient-primary rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{MOOD_EMOJIS[mood]}</span>
            <span className="text-sm opacity-90 font-medium">
              {t("prediction.yourPrediction") ||
                `${t("common.your")} ${t("prediction.title")}`}
            </span>
          </div>
          <div className="text-xs bg-white/20 px-3 py-1.5 rounded-full border border-white/20">
            {t("prediction.afcon")}
          </div>
        </div>

        <div className="bg-white/10 rounded-xl p-4 mb-4 border border-white/10">
          <div
            className="flex items-center justify-between gap-4"
            style={{ display: "flex", alignItems: "center" }}
          >
            <div
              className="flex-1 text-center"
              style={{ flex: "1 1 0%", minWidth: 0 }}
            >
              <div className="text-4xl mb-2" style={{ display: "block" }}>
                {homeFlag}
              </div>
              <div
                className="text-sm mb-3 font-medium"
                style={{ display: "block" }}
              >
                {translatedHomeTeam}
              </div>
              <div
                className="text-5xl font-bold bg-white/15 rounded-xl py-3 border border-white/10"
                style={{ display: "block" }}
              >
                {homeScore}
              </div>
            </div>

            <div
              className="px-4 py-2.5 bg-yellow-400 text-primary rounded-xl font-semibold text-sm shadow-sm"
              style={{ flexShrink: 0 }}
            >
              {t("match.vs")}
            </div>

            <div
              className="flex-1 text-center"
              style={{ flex: "1 1 0%", minWidth: 0 }}
            >
              <div className="text-4xl mb-2" style={{ display: "block" }}>
                {awayFlag}
              </div>
              <div
                className="text-sm mb-3 font-medium"
                style={{ display: "block" }}
              >
                {translatedAwayTeam}
              </div>
              <div
                className="text-5xl font-bold bg-white/15 rounded-xl py-3 border border-white/10"
                style={{ display: "block" }}
              >
                {awayScore}
              </div>
            </div>
          </div>
        </div>

        <div className="text-center space-y-1.5">
          <div className="text-xs opacity-75">{t("prediction.tournament")}</div>
          <div className="text-[10px] opacity-60 font-medium">
            {t("prediction.from") || "from"}{" "}
            <span className="font-semibold">afconpredict.com</span>
          </div>
        </div>
      </div>
    </div>
  );
}
